import { Button, Input } from '../../../shared';
import React, { FormEvent, useEffect, useState } from 'react';
import { addPromoCode, removePromoCode } from '../../../api/promoCodes';
import { useDispatch, useSelector } from 'react-redux';
import { handleToastAsyncAction } from '../../../utils/handleToastAsyncAction';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';

import { Wrapper } from './PromoCode.styled';
import { setCart } from '../../../store/cart/cartSlice';
import type { state } from '../../../types/store/state';
import { useTranslation } from 'next-i18next';

export const PromoCode = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [promoCode, setPromoCode] = useState('');
  const [isPromoCodeApplied, setIsPromoCodeApplied] = useState(false);

  const cart = useSelector(({ cart }: state) => cart);

  useEffect(() => {
    setIsPromoCodeApplied(
      cart.promotionalCode !== null &&
      (cart.promotionalCode.status as unknown as number) === 1
    );
    setPromoCode(cart.promotionalCode ? cart.promotionalCode.code : '');
  }, [cart]);

  const handlePromoCodeAction = async (
    action: 'add' | 'remove',
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const result = await handleToastAsyncAction(
      () => action === 'add'
        ? addPromoCode({ id: cart.reference, params: { code: promoCode } })
        : removePromoCode({ id: cart.reference, params: { code: promoCode } }),
      {
        onSuccessMessage: action === 'add' 
          ? t('checkout.promoCode.toastify.apply.success')
          : t('checkout.promoCode.toastify.remove.success'),
        showErrorToast: false,
        displayToastType: 'warning',
        dispatchAction: () => {
          if (action === 'add') {
            setIsPromoCodeApplied(true);
          } else {
            setPromoCode('');
            setIsPromoCodeApplied(false);
          }
        },
        errorLogger: (error) => {
          const defaultErrorMessage = t('checkout.promoCode.toastify.error');
          const promotionalCode = error.response?.data?.promotionalCode?.[0];
          const code = error.response?.data?.code?.[0];
          const finalErrorMessage = promotionalCode || code || defaultErrorMessage;

          toast.warning(finalErrorMessage);

          console.error('Error handling promo code:', error);
          Sentry.captureException(error, {
            tags: {
              action: action === 'add' ? 'add_promo_code' : 'remove_promo_code',
              cartReference: cart.reference,
              promoCode,
              statusCode: error?.response?.status,
              errorType: promotionalCode ? 'promotional_code' : code ? 'code' : 'generic'
            },
            extra: {
              promotionalCode,
              code,
              detail: error?.response?.data?.detail,
              finalErrorMessage
            }
          });
        }
      }
    );

    if (result.success && result.data) {
      dispatch(setCart(result.data));
    }
  };

  const handleApplyPromoCode = async (event: FormEvent<HTMLFormElement>) => {
    await handlePromoCodeAction('add', event);
  };

  const handleRemovePromoCode = async (event: FormEvent<HTMLFormElement>) => {
    await handlePromoCodeAction('remove', event);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPromoCode = e.target.value;
    setPromoCode(newPromoCode);
  };

  return (
    <Wrapper onSubmit={isPromoCodeApplied ? handleRemovePromoCode : handleApplyPromoCode}>
      <Input
        id="inputCode"
        label={isPromoCodeApplied
          ? t('checkout.promoCodeAdded.title')
          : t('checkout.promoCodeAdd.title')}
        name="code"
        type="text"
        placeholder={t('checkout.promoCode.placeholder')}
        value={promoCode}
        onChange={handleInputChange}
        disabled={isPromoCodeApplied}
      />
      <Button
        title={isPromoCodeApplied
          ? t('checkout.promoCode.ctaRemove.title')
          : t('checkout.promoCode.ctaApply.title')}
        type="submit"
      />
    </Wrapper>
  );
};
